import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Box,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  PowerSettingsNew as PowerIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import employeesManagementStore from '../stores/EmployeesManagementStore';
import adminStore from '../stores/AdminStore';
import EmployeeFormModal from '../components/EmployeeFormModal';
import LinkUserModal from '../components/LinkUserModal';
import WorkingHoursModal from '../components/WorkingHoursModal';

const EmployeesManagement = observer(() => {
  const {
    employees,
    isLoading,
    error,
    successMessage,
    isModalOpen,
    modalMode,
    currentEmployee
  } = employeesManagementStore;

  useEffect(() => {
    // Initialize stores when component mounts
    employeesManagementStore.init();
    adminStore.loadShopSettings();

    // Cleanup when component unmounts
    return () => {
      employeesManagementStore.dispose();
    };
  }, []);

  const handleCreateEmployee = () => {
    employeesManagementStore.openModal('create');
  };

  const handleEditEmployee = (employee) => {
    employeesManagementStore.openModal('edit', employee);
  };

  const handleLinkUser = (employee) => {
    employeesManagementStore.openModal('link', employee);
  };

  const handleDeleteEmployee = (employeeId) => {
    employeesManagementStore.deleteEmployee(employeeId);
  };

  const handleToggleStatus = (employeeId) => {
    employeesManagementStore.toggleEmployeeStatus(employeeId);
  };

  const handleUnlinkUser = (employeeId) => {
    employeesManagementStore.unlinkUserFromEmployee(employeeId);
  };

  const handleEditWorkingHours = (employee) => {
    employeesManagementStore.openModal('working-hours', employee);
  };

  const handleCloseSnackbar = () => {
    employeesManagementStore.clearMessages();
  };

  if (isLoading && employees.length === 0) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Gerenciar Funcionários
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateEmployee}
        >
          Novo Funcionário
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Apelido</TableCell>
              <TableCell>Telefone</TableCell>
              <TableCell>Serviços</TableCell>
              <TableCell>Conta de Usuário</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.map((employee) => (
              <TableRow key={employee.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: employee.color || '#ccc'
                      }}
                    />
                    {employee.name}
                  </Box>
                </TableCell>
                <TableCell>{employee.nickname || '-'}</TableCell>
                <TableCell>{employee.phone || '-'}</TableCell>
                <TableCell>
                  {employee.formattedServiceNames && employee.formattedServiceNames.length > 0 ? (
                    <Box>
                      {employee.formattedServiceNames.map((serviceName) => (
                        <Typography key={serviceName} variant="body2" sx={{ lineHeight: 1.5 }}>
                          {serviceName}
                        </Typography>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Nenhum</Typography>
                  )}
                </TableCell>
                <TableCell>
                  {employee.user ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        size="small"
                        label={employee.user.email}
                        onDelete={() => handleUnlinkUser(employee.id)}
                        deleteIcon={<LinkOffIcon />}
                      />
                    </Box>
                  ) : (
                    <Button
                      size="small"
                      startIcon={<LinkIcon />}
                      onClick={() => handleLinkUser(employee)}
                    >
                      Vincular
                    </Button>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={employee.active ? 'Ativo' : 'Inativo'}
                    color={employee.active ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => handleEditEmployee(employee)}
                    title="Editar"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleEditWorkingHours(employee)}
                    title="Editar Horários de Trabalho"
                  >
                    <ScheduleIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleToggleStatus(employee.id)}
                    title={employee.active ? 'Desativar' : 'Ativar'}
                  >
                    <PowerIcon color={employee.active ? 'error' : 'success'} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteEmployee(employee.id)}
                    title="Excluir"
                    disabled={employee.user !== null}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {employees.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Nenhum funcionário cadastrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Employee Form Modal */}
      {isModalOpen && modalMode !== 'link' && modalMode !== 'working-hours' && (
        <EmployeeFormModal
          open={isModalOpen}
          onClose={() => employeesManagementStore.closeModal()}
          mode={modalMode}
          employee={currentEmployee}
          store={employeesManagementStore}
          defaultWorkingHours={adminStore.shopSettings?.defaultWorkingHours}
        />
      )}

      {/* Link User Modal */}
      {isModalOpen && modalMode === 'link' && (
        <LinkUserModal
          open={isModalOpen}
          onClose={() => employeesManagementStore.closeModal()}
          employee={currentEmployee}
          store={employeesManagementStore}
        />
      )}

      {/* Working Hours Modal */}
      {isModalOpen && modalMode === 'working-hours' && (
        <WorkingHoursModal
          open={isModalOpen}
          onClose={() => employeesManagementStore.closeModal()}
          employee={currentEmployee}
          store={employeesManagementStore}
          defaultWorkingHours={adminStore.shopSettings?.defaultWorkingHours}
        />
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
});

export default EmployeesManagement;